import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Icon } from '@powdr/components';
import {
  Breakpoints, CarouselPaginationType,
  Components, FontFamily, FontSize, ThemeProps as Prop,
  TextTransformOptions,
} from '@powdr/constants';
import {
  componentColor, getFontStyles, themeUtil,
} from '@powdr/utils';

import 'swiper/css';

const CPNT = Components.CONTENT_CAROUSEL;

const INDICATOR_SLIDES_PER_VIEW = 3;

const StyledBulletPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  pointer-events: auto;
  gap: ${({ $smallGap }) => (($smallGap) ? 15 : 30)}px;
`;

const IndicatorSwiperOuter = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  pointer-events: auto;

  &.text-bullets-pagination.indicator-carousel-pagination {
    .primary-carousel-indicator-swiper {
      height: 100px;
    }

    .primary-carousel-indicator-swiper .swiper-wrapper {
      align-items: center;
      height: 100%;
    }

    .primary-carousel-indicator-swiper .swiper-slide {
      height: 100%;
      align-self: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .indicator-slide-inner {
      width: 100%;
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;
      padding: 0 6px;
    }

    .pagination-item {
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }

    .pagination-item button {
      width: 100%;
      min-width: 0;
      text-align: center;
    }

    .text-bullet {
      white-space: normal;
      overflow-wrap: break-word;
      word-break: break-word;
      line-height: 1.25;
    }
  }
`;

const IndicatorSlideInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  ${({ $fullWidth }) => (($fullWidth) ? 'width: 100%;' : 'width: auto; max-width: 100%;')}
  ${({ $padX }) => ($padX != null ? `padding: 0 ${$padX}px;` : '')}
`;

const SinglePaginationBullet = styled.div`
  cursor: pointer;
  width: fit-content;
  max-width: 100%;

  .standard-bullet {
    height: 15px;
    width: 15px;
    background-color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION, $componentTheme, Prop.BTN_BG) : themeUtil(Prop.BTN_BG, CPNT, $colorProfile))};
    border-radius: 50%;
    transition: all 0.25s ease-in;
  }

  .icon-bullet svg {
    transition: all 0.25s ease-in;
    fill: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION, $componentTheme, Prop.BTN_BG) : themeUtil(Prop.BTN_BG, CPNT, $colorProfile))};
    color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION, $componentTheme, Prop.BTN_BG) : themeUtil(Prop.BTN_BG, CPNT, $colorProfile))};
  }

  .text-bullet {
    ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.MED50, undefined, TextTransformOptions.UPPERCASE)};
    font-weight: bold;
    color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION, $componentTheme, Prop.BTN_BG) : themeUtil(Prop.BTN_BG, CPNT, $colorProfile))};
    line-height: 1.1;
    transition: all 0.25s ease-in;
    white-space: nowrap;

    ${Breakpoints.mobile(css`
      ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.MED20, undefined, TextTransformOptions.UPPERCASE)};
    `)}
  }

  &.active {
    .text-bullet {
      transition: all 0.25s ease-in;
      ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.LRG20, undefined, TextTransformOptions.UPPERCASE)};
      color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_ACTIVE, $componentTheme, Prop.BTN_BG_ACTIVE) : themeUtil(Prop.BTN_BG_ACTIVE, CPNT, $colorProfile))};

      ${Breakpoints.mobile(css`
        ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.LRG12, undefined, TextTransformOptions.UPPERCASE)};
      `)}
    }

    .icon-bullet svg {
      transition: all 0.25s ease-in;
      fill: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_ACTIVE, $componentTheme, Prop.BTN_BG_ACTIVE) : themeUtil(Prop.BTN_BG_ACTIVE, CPNT, $colorProfile))};
      color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_ACTIVE, $componentTheme, Prop.BTN_BG_ACTIVE) : themeUtil(Prop.BTN_BG_ACTIVE, CPNT, $colorProfile))};
    }

    .standard-bullet {
      transition: all 0.25s ease-in;
      background-color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_ACTIVE, $componentTheme, Prop.BTN_BG_ACTIVE) : themeUtil(Prop.BTN_BG_ACTIVE, CPNT, $colorProfile))};
    }
  }

  &:focus, &:focus-within, &:hover {
    .text-bullet {
      transition: all 0.25s ease-in;
      color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_HOVER, $componentTheme, Prop.BTN_BG_HOVER) : themeUtil(Prop.BTN_BG_HOVER, CPNT, $colorProfile))};
    }

    .icon-bullet svg {
      transition: all 0.25s ease-in;
      fill: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_HOVER, $componentTheme, Prop.BTN_BG_HOVER) : themeUtil(Prop.BTN_BG_HOVER, CPNT, $colorProfile))};
      color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_HOVER, $componentTheme, Prop.BTN_BG_HOVER) : themeUtil(Prop.BTN_BG_HOVER, CPNT, $colorProfile))};
    }

    .standard-bullet {
      transition: all 0.25s ease-in;
      background-color: ${({ $colorProfile, $componentTheme }) => (($componentTheme) ? componentColor(Prop.PAGINATION_HOVER, $componentTheme, Prop.BTN_BG_HOVER) : themeUtil(Prop.BTN_BG_HOVER, CPNT, $colorProfile))};
    }
  }
`;

const BulletButton = styled.button`

`;

const slideMainTo = (main, index, speed) => {
  if (!main || main.destroyed) return;
  if (main.params.loop) {
    main.slideToLoop(index, speed);
  } else {
    main.slideTo(index, speed);
  }
};

const slideIndicatorTo = (indicator, index, speed) => {
  if (!indicator || indicator.destroyed) return;
  if (indicator.params.loop) {
    indicator.slideToLoop(index, speed);
  } else {
    indicator.slideTo(index, speed);
  }
};

export const BulletPagination = ({
  className = '',
  bullets,
  colorProfile = null,
  componentTheme = null,
  activeIndex,
  carouselRef = null,
  type,
  scrollingIndicators = false,
  indicatorCarouselLoop = true,
  indicatorCarouselSpeed = 600,
}) => {
  const [indicatorSwiper, setIndicatorSwiper] = useState(null);

  const bulletCount = bullets?.length ?? 0;
  const useIndicatorLoop = indicatorCarouselLoop && bulletCount > INDICATOR_SLIDES_PER_VIEW;
  const slidePaddingX = type === CarouselPaginationType.BULLETS ? 6 : 12;
  const isTextIndicatorRow = type === CarouselPaginationType.TEXT_BULLETS;

  const paginationTypeClass = `${type}-pagination`;

  const handleIndicatorSwiper = (swiper) => {
    setIndicatorSwiper(swiper);
    requestAnimationFrame(() => {
      if (swiper?.destroyed) return;
      swiper.update();
      slideIndicatorTo(swiper, activeIndex, 0);
    });
  };

  useEffect(() => {
    if (!scrollingIndicators || !indicatorSwiper || !bulletCount) return;
    if (indicatorSwiper.destroyed) return;
    if (indicatorSwiper.realIndex === activeIndex) return;
    slideIndicatorTo(indicatorSwiper, activeIndex, indicatorCarouselSpeed);
  }, [activeIndex, bulletCount, indicatorCarouselSpeed, indicatorSwiper, scrollingIndicators]);

  useEffect(() => {
    if (!scrollingIndicators || !indicatorSwiper || indicatorSwiper.destroyed) return;
    const id = requestAnimationFrame(() => {
      indicatorSwiper.update();
    });
    return () => cancelAnimationFrame(id);
  }, [activeIndex, indicatorSwiper, scrollingIndicators]);

  const renderBullet = (bullet, idx) => (
    <SinglePaginationBullet
      key={`${bullet?.name || 'standard-bullet'}-${idx}`}
      className={classNames({
        active: idx === activeIndex,
      }, 'pagination-item')}
      $colorProfile={colorProfile}
      $componentTheme={componentTheme}
    >
      <BulletButton
        type="button"
        role="button"
        aria-label={`Navigate to Slide ${idx + 1}`}
        onClick={() => slideMainTo(carouselRef?.current, idx, indicatorCarouselSpeed)}
        tabIndex={0}
      >
        {(type === CarouselPaginationType.BULLETS) && (
        <div className="standard-bullet" />
        )}
        {(type === CarouselPaginationType.ICON_BULLETS) && (
        <Icon className="icon-bullet" name={bullet.name} height={bullet?.height || 50} />
        )}
        {(type === CarouselPaginationType.TEXT_BULLETS) && (
        <div className="text-bullet">{bullet.name}</div>
        )}
      </BulletButton>
    </SinglePaginationBullet>
  );

  if (scrollingIndicators && bulletCount > 0) {
    return (
      <IndicatorSwiperOuter
        className={classNames(className, paginationTypeClass, 'indicator-carousel-pagination')}
      >
        <Swiper
          className="primary-carousel-indicator-swiper"
          direction="horizontal"
          onSwiper={handleIndicatorSwiper}
          slidesPerView={INDICATOR_SLIDES_PER_VIEW}
          centeredSlides
          loop={useIndicatorLoop}
          speed={indicatorCarouselSpeed}
          spaceBetween={type === CarouselPaginationType.BULLETS ? 30 : 15}
          centerInsufficientSlides
          slideToClickedSlide
          watchSlidesProgress
          onSlideChange={(swiper) => {
            const main = carouselRef?.current;
            if (!main || main.destroyed) return;
            const ri = swiper.realIndex;
            if (main.realIndex === ri) return;
            slideMainTo(main, ri, indicatorCarouselSpeed);
          }}
        >
          {bullets.map((bullet, idx) => (
            <SwiperSlide key={`indicator-slide-${idx}`}>
              <IndicatorSlideInner
                className="indicator-slide-inner"
                $fullWidth={isTextIndicatorRow}
                $padX={isTextIndicatorRow ? null : slidePaddingX}
              >
                {renderBullet(bullet, idx)}
              </IndicatorSlideInner>
            </SwiperSlide>
          ))}
        </Swiper>
      </IndicatorSwiperOuter>
    );
  }

  return (
    <StyledBulletPagination
      className={classNames(className, paginationTypeClass)}
      $colorProfile={colorProfile}
      $componentTheme={componentTheme}
      $smallGap={type === CarouselPaginationType.BULLETS}
    >
      {bullets?.map((bullet, idx) => renderBullet(bullet, idx))}
    </StyledBulletPagination>
  );
};

BulletPagination.propTypes = {
  className: PropTypes.string,
  bullets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  colorProfile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  componentTheme: PropTypes.oneOf(Object.values(Components)),
  activeIndex: PropTypes.number.isRequired,
  carouselRef: PropTypes.shape({
    current: PropTypes.shape({
      slideToLoop: PropTypes.func,
      slideTo: PropTypes.func,
    }),
  }),
  type: PropTypes.oneOf(Object.values(CarouselPaginationType)).isRequired,
  scrollingIndicators: PropTypes.bool,
  indicatorCarouselLoop: PropTypes.bool,
  indicatorCarouselSpeed: PropTypes.number,
};
